import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Phone, MapPin, Euro, Clock, Send, ClipboardCheck, CheckCircle2,
  UserPlus, ChevronRight,
} from 'lucide-react';
import { tradeForSpec } from '@/lib/trades';

/**
 * Vue Kanban du pipeline de recrutement sous-traitants.
 * Colonnes :
 *   - Candidat détecté (prospect)   → email pas encore envoyé
 *   - Formulaire envoyé (en_attente_docs) → attend la réponse du candidat
 *   - En examen (en_examen)         → dossier soumis, à valider
 *   - Actif (actif)                 → référencé
 */
const COLUMNS = [
  {
    key: 'prospect',
    title: 'Candidat détecté',
    subtitle: 'Aucun email envoyé',
    icon: UserPlus,
    color: 'purple',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
    dot: 'bg-purple-500',
  },
  {
    key: 'en_attente_docs',
    title: 'Formulaire envoyé',
    subtitle: 'Attend la réponse',
    icon: Send,
    color: 'amber',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    dot: 'bg-amber-500',
  },
  {
    key: 'en_examen',
    title: 'En examen',
    subtitle: 'À valider par vous',
    icon: ClipboardCheck,
    color: 'blue',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
    dot: 'bg-blue-500',
  },
  {
    key: 'actif',
    title: 'Actif',
    subtitle: 'Référencé',
    icon: CheckCircle2,
    color: 'green',
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
    dot: 'bg-green-500',
  },
];

function timeAgo(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return "à l'instant";
    if (s < 3600) return `${Math.floor(s / 60)} min`;
    if (s < 86400) return `${Math.floor(s / 3600)} h`;
    const days = Math.floor(s / 86400);
    if (days < 30) return `${days} j`;
    return `${Math.floor(days / 30)} mois`;
  } catch (_e) { return null; }
}

export default function SousTraitantsKanban({ sousTraitants, alertsByST }) {
  const navigate = useNavigate();

  const grouped = useMemo(() => {
    const g = { prospect: [], en_attente_docs: [], en_examen: [], actif: [] };
    (sousTraitants || []).forEach((st) => {
      const s = st.statut_recrutement || 'actif';
      if (g[s]) g[s].push(st);
      else g.actif.push(st);
    });
    // Tri : plus récent d'abord (par date_creation ou date_soumission)
    Object.keys(g).forEach((k) => {
      g[k].sort((a, b) => {
        const da = a.date_soumission_dossier || a.date_creation || '';
        const db = b.date_soumission_dossier || b.date_creation || '';
        return db.localeCompare(da);
      });
    });
    return g;
  }, [sousTraitants]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="st-kanban">
      {COLUMNS.map((col) => {
        const items = grouped[col.key] || [];
        const Icon = col.icon;
        return (
          <div key={col.key} className={`rounded-xl border ${col.border} ${col.bg} overflow-hidden flex flex-col min-h-[400px]`} data-testid={`kanban-col-${col.key}`}>
            <div className="px-4 py-3 border-b border-current/10 flex items-center gap-2">
              <div className={`w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-4 h-4 ${col.text}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-bold ${col.text}`}>{col.title}</div>
                <div className="text-xs text-slate-600 truncate">{col.subtitle}</div>
              </div>
              <span className={`text-xs font-bold px-2 py-1 rounded-full bg-white ${col.text} shadow-sm`}>
                {items.length}
              </span>
            </div>

            <div className="flex-1 overflow-y-auto p-2 space-y-2 max-h-[calc(100vh-320px)]">
              {items.length === 0 ? (
                <div className="text-center py-8 text-xs text-slate-400">
                  Aucun {col.key === 'actif' ? 'sous-traitant' : 'candidat'}
                </div>
              ) : items.map((st) => {
                const trade = tradeForSpec(st.specialite);
                const TradeIcon = trade.icon;
                const dateRef = st.date_soumission_dossier || st.date_creation;
                const alerts = alertsByST?.[st.sous_traitant_id];
                return (
                  <button
                    key={st.sous_traitant_id}
                    onClick={() => navigate(`/sous-traitants/${st.sous_traitant_id}`)}
                    className="w-full text-left bg-white border border-slate-200 rounded-lg p-3 shadow-sm hover:shadow-md hover:border-slate-300 transition-all"
                    data-testid={`kanban-card-${st.sous_traitant_id}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className={`w-8 h-8 rounded-lg ${trade.bg} flex items-center justify-center flex-shrink-0`}>
                        <TradeIcon className={`w-4 h-4 ${trade.text}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 truncate text-sm">{st.nom}</div>
                        <div className={`text-[11px] px-1.5 py-0.5 rounded ${trade.bg} ${trade.text} inline-block mt-0.5 font-medium truncate max-w-full`}>
                          {trade.key}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                    </div>
                    <div className="mt-2 space-y-0.5 text-xs text-slate-600">
                      {st.telephone && (
                        <div className="flex items-center gap-1.5 truncate">
                          <Phone className="w-3 h-3 flex-shrink-0 text-slate-400" />
                          <span className="truncate">{st.telephone}</span>
                        </div>
                      )}
                      {st.zone && (
                        <div className="flex items-center gap-1.5 truncate">
                          <MapPin className="w-3 h-3 flex-shrink-0 text-slate-400" />
                          <span className="truncate">{st.zone}</span>
                        </div>
                      )}
                      {st.tarif_journalier && (
                        <div className="flex items-center gap-1.5">
                          <Euro className="w-3 h-3 flex-shrink-0 text-slate-400" />
                          <span>{st.tarif_journalier} €/j</span>
                        </div>
                      )}
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[10px]">
                      {dateRef && (
                        <span className="text-slate-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {timeAgo(dateRef)}
                        </span>
                      )}
                      {alerts && (alerts.expired + alerts.warning) > 0 && (
                        <span className={`px-1.5 py-0.5 rounded-full font-semibold ${alerts.expired > 0 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                          {alerts.expired + alerts.warning} doc{alerts.expired + alerts.warning > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
